import { clamp } from 'lodash';
import { atomFamily, selector, selectorFamily, type GetRecoilValue } from 'recoil';
import type { LayoutItem } from 'react-grid-layout';
import type { BlockId } from '../studio/types';
import { resetOnStudioReset } from '../effects/signalEffects';
import { blockContentSizeExceededState } from './blockContentSizeExceededState';
import { recoilBlockMaxSize } from './blockSize';
import {
  viewPages,
  studioPrintOrientationType,
  studioContentHeight,
  studioFooterHeight,
} from '../studio/configuration/print';
import {
  DEFAULT_LANDSCAPE_HEIGHT,
  DEFAULT_PORTRAIT_HEIGHT,
  PAGE_HEADER_HEIGHT,
  PRINT_GRID_HEADER_HEIGHT,
  RL_GRID_LANDSCAPE_NO_ROWS,
  RL_GRID_PORTRAIT_NO_ROWS,
} from '../studio/configuration/printConstants';
import { blockInfoGraphicType } from '../studio/configuration/blockConfig';
import { blockSettings } from '../studio/configuration/blockSettings';
import type { PrintOrientationTypeEnum } from 'venn-api';
import { PageType } from 'venn-utils';
import { isExpandableBlockType, isVirtualizedSliceBlockType } from './expandableBlockTypes';
import { continuationUserBlocksState, makeContinuationLayoutId } from './continuationLayoutId';

const RL_GRID_ROW_HEIGHT = 35;
const BLOCK_MARGIN = 7;
export const SLICE_FOOTER_CLEARANCE_PX = 12;

const PAGE_HEIGHT: Record<PrintOrientationTypeEnum, number> = {
  PORTRAIT: DEFAULT_PORTRAIT_HEIGHT,
  LANDSCAPE: DEFAULT_LANDSCAPE_HEIGHT,
};

const calcGridSliceMaxPx = (orientation: PrintOrientationTypeEnum, footerHeightPx: number): number =>
  (PAGE_HEIGHT[orientation] ?? PAGE_HEIGHT.PORTRAIT) - PAGE_HEADER_HEIGHT - BLOCK_MARGIN - footerHeightPx;

export const snapDownToRowMultiple = (px: number, rowHeightPx: number): number => {
  if (rowHeightPx <= 0) return Math.max(0, px);
  return Math.max(rowHeightPx, Math.floor(px / rowHeightPx) * rowHeightPx);
};

/**
 * Max data-rows region within a continuation slice — total slice height minus the repeated header band and footer
 * clearance, snapped down to a whole-row multiple so slice boundaries never cut through a data row.
 */
export const continuationDataMaxPx = ({
  orientation,
  columnHeaderHeightPx,
  footerHeightPx,
  dataRowHeightPx,
}: {
  orientation: PrintOrientationTypeEnum;
  columnHeaderHeightPx: number;
  footerHeightPx: number;
  dataRowHeightPx: number;
}): number => {
  const rawAvailable =
    calcGridSliceMaxPx(orientation, footerHeightPx) - SLICE_FOOTER_CLEARANCE_PX - columnHeaderHeightPx;
  return snapDownToRowMultiple(rawAvailable, dataRowHeightPx);
};

export const continuationSliceHeightPx = ({
  fullContentHeight,
  sourcePageVisibleHeight,
  continuationIndex,
  totalContinuations,
  orientation,
  columnHeaderHeightPx,
  footerHeightPx,
  dataRowHeightPx,
}: {
  fullContentHeight: number;
  sourcePageVisibleHeight: number;
  continuationIndex: number;
  totalContinuations: number;
  orientation: PrintOrientationTypeEnum;
  columnHeaderHeightPx: number;
  footerHeightPx: number;
  dataRowHeightPx: number;
}): number => {
  const dataMax = continuationDataMaxPx({ orientation, columnHeaderHeightPx, footerHeightPx, dataRowHeightPx });
  const isLast = continuationIndex === totalContinuations - 1;
  if (!isLast) {
    return dataMax + columnHeaderHeightPx;
  }
  const dataOffset = sourcePageVisibleHeight + continuationIndex * dataMax;
  const remainingData = Math.max(fullContentHeight - dataOffset, 0);
  return Math.min(dataMax, remainingData) + columnHeaderHeightPx;
};

export const continuationSliceRows = ({
  sliceHeightPx,
  pageRows,
}: {
  sliceHeightPx: number;
  pageRows: number;
}): number => {
  const rows = Math.max(1, Math.ceil((sliceHeightPx + BLOCK_MARGIN) / (RL_GRID_ROW_HEIGHT + BLOCK_MARGIN)));
  return Math.min(rows, pageRows);
};

const synthesizeUniformBoundaries = (fullDataHeight: number, rowHeightPx: number): number[] => {
  if (rowHeightPx <= 0) return [fullDataHeight];
  const boundaries: number[] = [];
  for (let b = rowHeightPx; b < fullDataHeight; b += rowHeightPx) {
    boundaries.push(b);
  }
  boundaries.push(fullDataHeight);
  return boundaries;
};

export const floorToBoundaryIndex = (boundaries: number[], limit: number): number => {
  let lo = 0;
  let hi = boundaries.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (boundaries[mid]! > limit) {
      hi = mid;
    } else {
      lo = mid + 1;
    }
  }
  return lo;
};

export const floorToBoundary = (boundaries: number[], limit: number): number => {
  const index = floorToBoundaryIndex(boundaries, limit);
  return index > 0 ? boundaries[index - 1]! : 0;
};

const nextBoundaryAbove = (boundaries: number[], limit: number): number | undefined =>
  boundaries.find((boundary) => boundary > limit);

/**
 * Pure core of the continuation-slicing math. Cuts a block's data content into page-sized slices
 * at real measured row boundaries (a greedy walk) instead of pixel math derived from a single
 * sampled row height, so a row taller than average is never split across two pages. When no
 * boundaries have been measured yet, falls back to a uniform grid of `rowHeightPx`-spaced
 * boundaries that reproduces the pre-existing pixel-math results exactly.
 */
export const computeBlockSliceLayout = ({
  boundaries,
  sourceDataAreaPx,
  dataPerSlicePx,
  fullDataHeight,
  rowHeightPx,
  footerClearancePx,
  tailReservePx = 0,
}: {
  boundaries: number[];
  sourceDataAreaPx: number;
  dataPerSlicePx: number;
  fullDataHeight: number;
  rowHeightPx: number;
  footerClearancePx: number;
  tailReservePx?: number;
}): { sourceVisibleDataPx: number; cutOffsetsPx: number[]; contentEndPx: number } => {
  const dataBoundaries =
    boundaries.length > 0 && fullDataHeight > 0 ? boundaries : synthesizeUniformBoundaries(fullDataHeight, rowHeightPx);
  const dataEndPx = dataBoundaries[dataBoundaries.length - 1] ?? 0;

  // Append the below-data tail (trailer + pinned bottom row) as one indivisible boundary so the greedy
  // walk reveals it on the last slice and spills it to a new page when it does not fit under the rows.
  const effectiveBoundaries = tailReservePx > 0 ? [...dataBoundaries, dataEndPx + tailReservePx] : dataBoundaries;
  const contentEndPx = effectiveBoundaries[effectiveBoundaries.length - 1] ?? 0;

  const wholeRows = floorToBoundary(dataBoundaries, sourceDataAreaPx);
  if (contentEndPx <= wholeRows) {
    return { sourceVisibleDataPx: contentEndPx, cutOffsetsPx: [], contentEndPx };
  }

  const packBudgetPx = Math.max(0, sourceDataAreaPx - footerClearancePx);
  let sourceVisibleDataPx = floorToBoundary(dataBoundaries, packBudgetPx);

  const cutOffsetsPx: number[] = [];
  let start = sourceVisibleDataPx;
  while (start < contentEndPx) {
    cutOffsetsPx.push(start);
    let next = floorToBoundary(effectiveBoundaries, start + dataPerSlicePx);
    if (next <= start) {
      next = nextBoundaryAbove(effectiveBoundaries, start) ?? contentEndPx;
    }
    start = next;
  }

  // De-orphan the tail: if the final slice would contain only the tail (its start sits exactly on the last
  // data boundary), pull the final cut back onto data boundaries so the tail rides with as many trailing
  // rows as fit on one page — never a lonely tail page, and the prior page stays filled.
  if (tailReservePx > 0 && cutOffsetsPx.length > 0 && cutOffsetsPx[cutOffsetsPx.length - 1] === dataEndPx) {
    const prevCut = cutOffsetsPx.length > 1 ? (cutOffsetsPx[cutOffsetsPx.length - 2] ?? 0) : 0;
    const minStart = dataEndPx + tailReservePx - dataPerSlicePx;
    let pulled: number | undefined;
    for (let i = dataBoundaries.length - 1; i >= 0; i--) {
      const b = dataBoundaries[i]!;
      if (b > prevCut && b < dataEndPx && b >= minStart) {
        pulled = b;
        break;
      }
    }
    if (pulled !== undefined) {
      cutOffsetsPx[cutOffsetsPx.length - 1] = pulled;
      if (cutOffsetsPx.length === 1 && sourceVisibleDataPx > pulled) {
        sourceVisibleDataPx = pulled;
      }
    } else {
      cutOffsetsPx.pop();
    }
  }

  return { sourceVisibleDataPx, cutOffsetsPx, contentEndPx };
};

export const blockExpandedState = atomFamily<boolean, BlockId>({
  key: 'blockExpanded',
  default: false,
  effects: [resetOnStudioReset],
});

export const expandedBlockIdsState = selectorFamily<BlockId[], readonly BlockId[]>({
  key: 'expandedBlockIds',
  get:
    (blockIds) =>
    ({ get }) =>
      blockIds.filter((id) => get(blockExpandedState(id))),
});

export interface BlockOriginalLayout {
  readonly x: number;
  readonly w: number;
  // Only continuation-page blocks store their expanded height on the layout item, so only they need it restored.
  readonly h: number | undefined;
}

export const blockOriginalLayoutState = atomFamily<BlockOriginalLayout | undefined, BlockId>({
  key: 'blockOriginalLayout',
  default: undefined,
  effects: [resetOnStudioReset],
});

export const blockFullContentHeightState = atomFamily<number, BlockId>({
  key: 'blockFullContentHeight',
  default: 0,
});

export const blockPersistedContinuationPageCountState = atomFamily<number, BlockId>({
  key: 'blockPersistedContinuationPageCount',
  default: 0,
  effects: [resetOnStudioReset],
});

/**
 * Measured pixel height of the source block's AG-Grid column-header row
 * (typically ~26px — `PRINT_GRID_HEADER_HEIGHT` — but can vary per block /
 * theme / wrapped-text). Set by `ReportLabBlockContainer`'s `ResizeObserver`
 * on the `.ag-header` element. Consumed by the continuation slice math so we
 * size the sticky-header band and the data-rows slice correctly without
 * hard-coding a fallback that's wrong for some blocks.
 */
export const blockColumnHeaderHeightState = atomFamily<number, BlockId>({
  key: 'blockColumnHeaderHeight',
  default: PRINT_GRID_HEADER_HEIGHT,
});

/**
 * Column widths, keyed by AG-Grid column id, that the block's full-content instance settled on after
 * auto-sizing. AG-Grid auto-sizes a column from the rows it has actually rendered, so a row-virtualized
 * continuation slice measuring its own few dozen rows lands on narrower columns than the source page and
 * the two stop lining up. Slices replay these instead of trusting their own measurement.
 */
export const blockGridColumnWidthsState = atomFamily<Readonly<Record<string, number>>, BlockId>({
  key: 'blockGridColumnWidths',
  default: {},
});

/**
 * Measured pixel height of the source block's general header (title /
 * subtitle / metadata / movement controls — the chrome above the AG-Grid).
 * On continuation slices `<Block.Header>` returns null, so the block content
 * shifts up by this many pixels relative to the source-page render. The slice
 * math subtracts this value when computing scroll offsets.
 */
export const blockTitleBarHeightState = atomFamily<number, BlockId>({
  key: 'blockTitleBarHeight',
  default: 0,
});

/**
 * Measured pixel height of a single AG-Grid data row inside the source block.
 * Derived from `--ag-row-height` via the first `.ag-row` element under the
 * block's `.ag-center-cols-container`. Defaults to `RL_GRID_ROW_HEIGHT` (35)
 * so behavior is unchanged before the first measurement lands and so blocks
 * without rendered rows fall back to the RGL cell height. Slice math uses
 * this to snap `sourcePageVisibleHeight` and `dataPerSlicePx` to whole-row
 * multiples so rows are never cut at slice boundaries.
 */
export const blockDataRowHeightState = atomFamily<number, BlockId>({
  key: 'blockDataRowHeight',
  default: RL_GRID_ROW_HEIGHT,
});

/**
 * Measured pixel height of everything below the block's last data row — the pinned bottom "Total" row,
 * the AG-Grid `.ag-floating-bottom` border/gutter, the horizontal-scroll spacer, and any trailer. Read as a
 * single same-frame DOM measurement (last-row bottom to content bottom) so it can't skew. Default 0.
 */
export const blockBelowDataTailHeightState = atomFamily<number, BlockId>({
  key: 'blockBelowDataTailHeight',
  default: 0,
});

/**
 * Height in pixels of the non-repeating content rendered above the AG-Grid table on the source
 * page only (e.g. chart + separator for Peer Group Analysis). Defaults to 0 so all existing
 * grid blocks are unaffected.
 */
export const blockPrefaceHeightState = atomFamily<number, BlockId>({
  key: 'blockPrefaceHeight',
  default: 0,
});

/**
 * Height in pixels of the content rendered below the AG-Grid table (e.g. footer + disclaimer
 * for Peer Group Analysis). Defaults to 0 so all existing grid blocks are unaffected.
 */
export const blockTrailerHeightState = atomFamily<number, BlockId>({
  key: 'blockTrailerHeight',
  default: 0,
});

/**
 * Ascending cumulative data-row bottom offsets, measured from the top of the first `.ag-row`
 * inside the source block. The last entry equals the total data height. Empty means no
 * measurement has landed yet, so slice math falls back to a uniform-row-height approximation.
 */
export const blockDataRowBoundariesState = atomFamily<number[], BlockId>({
  key: 'blockDataRowBoundaries',
  default: [],
  effects: [resetOnStudioReset],
});

/**
 * Total data-row count reported by the block's content component (e.g. Tax Impact publishes
 * rowData.length). For uniform-row virtualized-slice blocks this lets pagination be derived from
 * data instead of a DOM measurement that only the source page can make, so the page count stays
 * correct while the source page is unmounted by virtualization. Default 0 = not reported.
 */
export const blockDataRowCountState = atomFamily<number, BlockId>({
  key: 'blockDataRowCount',
  default: 0,
});

export const blockCanExpandState = selectorFamily<boolean, BlockId>({
  key: 'blockCanExpand',
  get:
    (blockId) =>
    ({ get }) => {
      const setting = get(blockSettings(blockId));
      const infoGraphicType = get(blockInfoGraphicType(blockId));
      if (!isExpandableBlockType({ customBlockType: setting?.customBlockType, infoGraphicType })) {
        return false;
      }

      const maxSize = get(recoilBlockMaxSize.rawState(blockId));
      const prefaceHeight = get(blockPrefaceHeightState(blockId));

      // When a block has a non-repeating preface (e.g. Peer Group chart), expansion is only
      // meaningful when the table overflows — not when the chart itself is cut off. Suppress
      // expansion when the preface alone exceeds the available content area so the red Data
      // Overflow indicator is shown instead (AC #1).
      if (prefaceHeight > 0 && maxSize != null) {
        const titleBarHeight = get(blockTitleBarHeightState(blockId));
        if (prefaceHeight > maxSize.height - titleBarHeight) {
          return false;
        }
      }

      const sizeExceeded = get(blockContentSizeExceededState(blockId));
      const fullContentHeight = get(blockFullContentHeightState(blockId));
      const titleBarHeight = get(blockTitleBarHeightState(blockId));
      const contentRegionNaturalHeight = fullContentHeight - titleBarHeight;
      const hasHeightOverflow =
        sizeExceeded.height ||
        (fullContentHeight > 0 &&
          maxSize != null &&
          Math.floor(contentRegionNaturalHeight) > Math.ceil(maxSize.height));
      return hasHeightOverflow;
    },
});

const calcGridItemHeight = (h: number): number => h * RL_GRID_ROW_HEIGHT + Math.max(0, h - 1) * BLOCK_MARGIN;

export const studioMaxUsableRows = selector<number>({
  key: 'studioMaxUsableRows',
  get: ({ get }) => {
    const contentHeight = get(studioContentHeight);
    return Math.floor((contentHeight + BLOCK_MARGIN) / (RL_GRID_ROW_HEIGHT + BLOCK_MARGIN));
  },
});

// Capped at the grid's row count because RL_GRID_*_NO_ROWS sizes the grid container; a short footer leaves space
// below it that blocks can't use.
export const studioPageUsableRows = selector<number>({
  key: 'studioPageUsableRows',
  get: ({ get }) => {
    const orientation = get(studioPrintOrientationType);
    const noRows = orientation === 'PORTRAIT' ? RL_GRID_PORTRAIT_NO_ROWS : RL_GRID_LANDSCAPE_NO_ROWS;
    return Math.min(noRows, get(studioMaxUsableRows));
  },
});

export const calcExpandedRows = ({
  availableRows,
  originalRows,
  fullContentHeight,
}: {
  availableRows: number;
  originalRows: number;
  fullContentHeight: number;
}): number => {
  if (fullContentHeight <= 0) return availableRows;
  const contentNeededRows = Math.ceil((fullContentHeight + BLOCK_MARGIN) / (RL_GRID_ROW_HEIGHT + BLOCK_MARGIN));
  return Math.max(originalRows, Math.min(contentNeededRows, availableRows));
};

const findHostedLayoutUnderOwner = (
  get: GetRecoilValue,
  ownerId: BlockId,
  blockId: BlockId,
  visited: Set<BlockId>,
): LayoutItem | undefined => {
  if (visited.has(ownerId)) return undefined;
  visited.add(ownerId);
  const continuationCount = get(blockContinuationPageCount(ownerId));
  for (let continuationIndex = 0; continuationIndex < continuationCount; continuationIndex++) {
    const sliceLayoutId = makeContinuationLayoutId({ sourceBlockId: ownerId, continuationIndex });
    for (const layout of get(continuationUserBlocksState(sliceLayoutId))) {
      if (layout.i === blockId) return layout;
      const nested = findHostedLayoutUnderOwner(get, layout.i, blockId, visited);
      if (nested) return nested;
    }
  }
  return undefined;
};

// Walks owners in page order and stops at the target, so a block's lookup only reads the page counts of blocks placed
// before it; those never depend back on it, which keeps expanded blocks sharing a continuation page free of cycles.
const findContinuationHostedLayout = (get: GetRecoilValue, blockId: BlockId): LayoutItem | undefined => {
  const visited = new Set<BlockId>();
  for (const page of get(viewPages)) {
    if (page.type !== PageType.GRID) continue;
    for (const layout of page.layout) {
      const hosted = findHostedLayoutUnderOwner(get, layout.i, blockId, visited);
      if (hosted) return hosted;
    }
  }
  return undefined;
};

export const blockExpandedRows = selectorFamily<number, BlockId>({
  key: 'blockExpandedRows',
  get:
    (blockId) =>
    ({ get }) => {
      const pages = get(viewPages);
      for (const page of pages) {
        if (page.type !== PageType.GRID) continue;
        const layout = page.layout.find((l) => l.i === blockId);
        if (!layout) continue;

        const maxUsableRows = get(studioMaxUsableRows);
        return calcExpandedRows({
          availableRows: Math.max(layout.h, maxUsableRows - layout.y),
          originalRows: layout.h,
          fullContentHeight: get(blockFullContentHeightState(blockId)),
        });
      }

      const hostedLayout = findContinuationHostedLayout(get, blockId);
      if (!hostedLayout) return 0;

      return calcExpandedRows({
        availableRows: Math.max(hostedLayout.h, get(studioPageUsableRows) - hostedLayout.y),
        originalRows: hostedLayout.h,
        fullContentHeight: get(blockFullContentHeightState(blockId)),
      });
    },
});

export const blockEffectiveFullContentHeight = selectorFamily<number, BlockId>({
  key: 'blockEffectiveFullContentHeight',
  get:
    (blockId) =>
    ({ get }) => {
      // count is checked before blockSettings so a block that never publishes a row count (every
      // non-virtualized-slice block, and every snapshot that predates this atom) never reads
      // blockSettings, which requires auth and would otherwise suspend on a pending promise.
      const count = get(blockDataRowCountState(blockId));
      const isVirtualizedSlice =
        count > 0 &&
        isVirtualizedSliceBlockType({
          customBlockType: get(blockSettings(blockId))?.customBlockType,
          infoGraphicType: get(blockInfoGraphicType(blockId)),
        });
      if (!isVirtualizedSlice) return get(blockFullContentHeightState(blockId));

      const titleBarPx = get(blockTitleBarHeightState(blockId));
      const prefacePx = get(blockPrefaceHeightState(blockId));
      const columnHeaderPx = get(blockColumnHeaderHeightState(blockId));
      const rowHeightPx = get(blockDataRowHeightState(blockId));
      const belowDataTailPx = get(blockBelowDataTailHeightState(blockId));
      return titleBarPx + prefacePx + columnHeaderPx + count * rowHeightPx + belowDataTailPx;
    },
});

export const blockContentOverflowsRows = selectorFamily<boolean, { blockId: BlockId; rows: number }>({
  key: 'blockContentOverflowsRows',
  get:
    ({ blockId, rows }) =>
    ({ get }) => {
      const setting = get(blockSettings(blockId));
      const infoGraphicType = get(blockInfoGraphicType(blockId));
      if (!isExpandableBlockType({ customBlockType: setting?.customBlockType, infoGraphicType })) return false;
      return get(blockEffectiveFullContentHeight(blockId)) > calcGridItemHeight(rows);
    },
});

export const blockEffectiveBoundaries = selectorFamily<number[], BlockId>({
  key: 'blockEffectiveBoundaries',
  get:
    (blockId) =>
    ({ get }) => {
      const count = get(blockDataRowCountState(blockId));
      const isVirtualizedSlice =
        count > 0 &&
        isVirtualizedSliceBlockType({
          customBlockType: get(blockSettings(blockId))?.customBlockType,
          infoGraphicType: get(blockInfoGraphicType(blockId)),
        });
      if (!isVirtualizedSlice) return get(blockDataRowBoundariesState(blockId));

      const rowHeightPx = get(blockDataRowHeightState(blockId));
      return synthesizeUniformBoundaries(count * rowHeightPx, rowHeightPx);
    },
});

export const blockSourcePageVisibleHeight = selectorFamily<number, BlockId>({
  key: 'blockSourcePageVisibleHeight',
  get:
    (blockId) =>
    ({ get }) => {
      const expandedH = get(blockExpandedRows(blockId));
      const titleBarPx = get(blockTitleBarHeightState(blockId));
      const prefacePx = get(blockPrefaceHeightState(blockId));
      const columnHeaderPx = get(blockColumnHeaderHeightState(blockId));
      const rowHeightPx = get(blockDataRowHeightState(blockId));
      const trailerPx = get(blockTrailerHeightState(blockId));
      const fullContentHeight = get(blockEffectiveFullContentHeight(blockId));
      const containerPx = calcGridItemHeight(expandedH);

      const dataAreaPx = Math.max(0, containerPx - titleBarPx - prefacePx - columnHeaderPx);
      if (prefacePx > 0 && dataAreaPx < rowHeightPx) return titleBarPx + prefacePx;
      if (trailerPx > 0 && fullContentHeight <= containerPx) return fullContentHeight;

      const { sourceVisibleDataPx } = get(blockSliceLayoutResult(blockId));
      return titleBarPx + prefacePx + columnHeaderPx + sourceVisibleDataPx;
    },
});

export const blockSourceLayoutState = selectorFamily<LayoutItem | undefined, BlockId>({
  key: 'blockSourceLayout',
  get:
    (blockId) =>
    ({ get }) => {
      const pages = get(viewPages);
      for (const page of pages) {
        if (page.type !== PageType.GRID) continue;
        const layout = page.layout.find((l) => l.i === blockId);
        if (layout) return layout;
      }
      return undefined;
    },
});

const blockContinuationSliceInputs = selectorFamily<
  {
    boundaries: number[];
    dataAreaPx: number;
    dataPerSlicePx: number;
    fullDataHeight: number;
    rowHeightPx: number;
    columnHeaderHeightPx: number;
    trailerPx: number;
  },
  BlockId
>({
  key: 'blockContinuationSliceInputs',
  get:
    (blockId) =>
    ({ get }) => {
      const expandedH = get(blockExpandedRows(blockId));
      const titleBarPx = get(blockTitleBarHeightState(blockId));
      const prefacePx = get(blockPrefaceHeightState(blockId));
      const columnHeaderHeightPx = get(blockColumnHeaderHeightState(blockId));
      const rowHeightPx = get(blockDataRowHeightState(blockId));
      const trailerPx = get(blockTrailerHeightState(blockId));
      const fullContentHeight = get(blockEffectiveFullContentHeight(blockId));
      const containerPx = calcGridItemHeight(expandedH);
      const orientation = get(studioPrintOrientationType);
      const footerHeightPx = get(studioFooterHeight);

      const dataAreaPx = Math.max(0, containerPx - titleBarPx - prefacePx - columnHeaderHeightPx);
      const dataPerSlicePx = continuationDataMaxPx({
        orientation,
        columnHeaderHeightPx,
        footerHeightPx,
        dataRowHeightPx: rowHeightPx,
      });
      const fullDataHeight = Math.max(0, fullContentHeight - titleBarPx - prefacePx - columnHeaderHeightPx - trailerPx);

      return {
        boundaries: get(blockEffectiveBoundaries(blockId)),
        dataAreaPx,
        dataPerSlicePx,
        fullDataHeight,
        rowHeightPx,
        columnHeaderHeightPx,
        trailerPx,
      };
    },
});

const blockSliceLayoutResult = selectorFamily<
  { sourceVisibleDataPx: number; cutOffsetsPx: number[]; contentEndPx: number },
  BlockId
>({
  key: 'blockSliceLayoutResult',
  get:
    (blockId) =>
    ({ get }) => {
      const { boundaries, dataAreaPx, dataPerSlicePx, fullDataHeight, rowHeightPx, trailerPx } = get(
        blockContinuationSliceInputs(blockId),
      );
      const tailReservePx = trailerPx > 0 ? get(blockBelowDataTailHeightState(blockId)) : 0;
      return computeBlockSliceLayout({
        boundaries,
        sourceDataAreaPx: dataAreaPx,
        dataPerSlicePx,
        fullDataHeight,
        rowHeightPx,
        footerClearancePx: SLICE_FOOTER_CLEARANCE_PX,
        tailReservePx,
      });
    },
});

export const blockContinuationPageCount = selectorFamily<number, BlockId>({
  key: 'blockContinuationPageCount',
  get:
    (blockId) =>
    ({ get }) => {
      const isExpanded = get(blockExpandedState(blockId));
      if (!isExpanded) return 0;

      const fullContentHeight = get(blockEffectiveFullContentHeight(blockId));
      if (!fullContentHeight) {
        // Pre-measurement fallback: the virtualized source block has not mounted yet, so no height is
        // measured. Use persisted count so the Outline panel shows expanded pages for saved view; the live
        // computation below overrides it once a real height is measured.
        return get(blockPersistedContinuationPageCountState(blockId));
      }

      const sourceVisibleHeight = get(blockSourcePageVisibleHeight(blockId));
      if (fullContentHeight <= sourceVisibleHeight) return 0;

      return get(blockSliceLayoutResult(blockId)).cutOffsetsPx.length;
    },
});

export interface BlockContinuationSlice {
  readonly scrollOffsetPx: number;
  readonly sliceHeightPx: number;
  readonly sliceRowCount: number;
  readonly sliceStartRowIndex: number;
  /**
   * Height of just the data band this slice covers, before any column header is added on top. A
   * renderer that sizes its own grid chrome needs this rather than `sliceHeightPx`, which already
   * bakes in the column-header height this math assumed.
   */
  readonly sliceDataHeightPx: number;
}

export const blockContinuationSlices = selectorFamily<BlockContinuationSlice[], BlockId>({
  key: 'blockContinuationSlices',
  get:
    (blockId) =>
    ({ get }) => {
      const { boundaries, columnHeaderHeightPx, trailerPx, rowHeightPx, fullDataHeight } = get(
        blockContinuationSliceInputs(blockId),
      );
      const { cutOffsetsPx, contentEndPx } = get(blockSliceLayoutResult(blockId));

      const orientation = get(studioPrintOrientationType);
      const footerHeightPx = get(studioFooterHeight);
      const pageRows = orientation === 'PORTRAIT' ? RL_GRID_PORTRAIT_NO_ROWS : RL_GRID_LANDSCAPE_NO_ROWS;

      // Trailer blocks already bake the tail into contentEndPx (reserve path); only pinned-Total / no-trailer
      // blocks need the tail revealed here. Ignore sub-row grid chrome (borders/scrollbar) so plain grids with
      // no pinned row are untouched.
      const rawTailPx = trailerPx > 0 ? 0 : get(blockBelowDataTailHeightState(blockId));
      const revealTailPx = rawTailPx >= rowHeightPx / 2 ? rawTailPx : 0;
      const pageSliceMaxPx = calcGridSliceMaxPx(orientation, footerHeightPx);

      // Index against real data-row boundaries only, never the synthetic below-data tail boundary
      // computeBlockSliceLayout appends internally (dataEndPx + tailReservePx) — that entry isn't a
      // row, so counting boundaries against it would overstate the row index.
      const dataBoundaries =
        boundaries.length > 0 && fullDataHeight > 0
          ? boundaries
          : synthesizeUniformBoundaries(fullDataHeight, rowHeightPx);

      return cutOffsetsPx.map((scrollOffsetPx, i) => {
        const end = cutOffsetsPx[i + 1] ?? contentEndPx;
        const isLast = i === cutOffsetsPx.length - 1;
        const baseHeightPx = end - scrollOffsetPx + columnHeaderHeightPx;
        const sliceHeightPx = isLast ? Math.min(baseHeightPx + revealTailPx, pageSliceMaxPx) : baseHeightPx;
        const sliceStartRowIndex = clamp(
          floorToBoundaryIndex(dataBoundaries, scrollOffsetPx),
          0,
          dataBoundaries.length,
        );
        return {
          scrollOffsetPx,
          sliceHeightPx,
          sliceRowCount: continuationSliceRows({ sliceHeightPx, pageRows }),
          sliceStartRowIndex,
          sliceDataHeightPx: sliceHeightPx - columnHeaderHeightPx,
        };
      });
    },
});
